import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { resellerAPI } from '../../services/api';
import { Eye, EyeOff } from 'lucide-react';

const VisibilityManagerModal = ({ open, onClose, resellers, onUpdated }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all' | 'visible' | 'hidden'
  const [updating, setUpdating] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return resellers.filter((r) => {
      if (filter === 'visible' && !r.can_view_customers) return false;
      if (filter === 'hidden' && r.can_view_customers) return false;
      if (!q) return true;
      return (
        (r.nickname || '').toLowerCase().includes(q) ||
        (r.kako_id || '').includes(q)
      );
    });
  }, [resellers, search, filter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAllFiltered = (checked) => {
    const next = new Set(selected);
    filtered.forEach((r) => {
      if (checked) next.add(r.id);
      else next.delete(r.id);
    });
    setSelected(next);
  };

  const applyBulk = async (canView) => {
    if (selected.size === 0) {
      toast.error('Selecione ao menos 1 afiliado');
      return;
    }
    setUpdating(true);
    try {
      const ids = Array.from(selected);
      const res = await resellerAPI.bulkUpdateVisibility(ids, canView);
      toast.success(`${res.data.updated} afiliado(s) atualizados`);
      setSelected(new Set());
      try {
        await onUpdated?.();
      } catch (refreshErr) {
        // refresh falhou mas o update funcionou; não exibe erro
        console.warn('Refresh após update falhou:', refreshErr);
      }
    } catch (e) {
      console.error('bulk visibility error:', e?.response?.status, e?.response?.data, e?.message);
      const msg = e?.response?.data?.detail || e?.message || 'Erro desconhecido';
      toast.error(`Erro ao atualizar visibilidade: ${msg}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-600" />
            Visibilidade de Clientes (em massa)
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-gray-500">
            Controle quais afiliados conseguem ver o KAKO ID dos clientes nas vendas.
            Selecione vários e aplique de uma vez.
          </DialogDescription>
        </DialogHeader>

        {/* Filtros */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <Input
            placeholder="🔍 Buscar por nome ou KAKO ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="visibility-search"
          />
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className="text-xs"
            >
              Todos
            </Button>
            <Button
              size="sm"
              variant={filter === 'visible' ? 'default' : 'outline'}
              onClick={() => setFilter('visible')}
              className="text-xs"
            >
              👁️ Visíveis
            </Button>
            <Button
              size="sm"
              variant={filter === 'hidden' ? 'default' : 'outline'}
              onClick={() => setFilter('hidden')}
              className="text-xs"
            >
              🔒 Ocultos
            </Button>
          </div>
        </div>

        {/* Lista com scroll */}
        <div className="flex-1 overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="p-2 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={(e) => toggleAllFiltered(e.target.checked)}
                    data-testid="visibility-toggle-all"
                  />
                </th>
                <th className="text-left p-2">Afiliado</th>
                <th className="text-left p-2">KAKO ID</th>
                <th className="text-center p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 py-6">
                    Nenhum afiliado encontrado
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b hover:bg-purple-50/40 cursor-pointer ${selected.has(r.id) ? 'bg-purple-50' : ''}`}
                    onClick={() => toggleOne(r.id)}
                  >
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleOne(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`visibility-checkbox-${r.id}`}
                      />
                    </td>
                    <td className="p-2 font-medium">{r.nickname || '-'}</td>
                    <td className="p-2 text-gray-500">{r.kako_id || '-'}</td>
                    <td className="p-2 text-center">
                      {r.can_view_customers ? (
                        <Badge className="bg-green-600 text-white text-[10px]">
                          <Eye className="w-3 h-3 mr-1" /> Visível
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-600 text-[10px]">
                          <EyeOff className="w-3 h-3 mr-1" /> Oculto
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Barra de ações */}
        <div className="border-t pt-3 flex flex-col sm:flex-row gap-2 items-center justify-between">
          <span className="text-sm text-gray-600">
            <strong>{selected.size}</strong> selecionado(s) de {filtered.length} listado(s)
          </span>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelected(new Set())}
              disabled={selected.size === 0 || updating}
              className="flex-1 sm:flex-initial text-xs"
            >
              Limpar seleção
            </Button>
            <Button
              size="sm"
              className="bg-gray-700 hover:bg-gray-800 text-white flex-1 sm:flex-initial text-xs"
              onClick={() => applyBulk(false)}
              disabled={selected.size === 0 || updating}
              data-testid="bulk-hide-btn"
            >
              🔒 Ocultar
            </Button>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-initial text-xs"
              onClick={() => applyBulk(true)}
              disabled={selected.size === 0 || updating}
              data-testid="bulk-show-btn"
            >
              👁️ Tornar Visível
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisibilityManagerModal;
